<script setup lang="ts">
import { getSource, getWatermark } from '@/charts'
import { height, useImageExport, width } from '@/composables/useImageExport'
import { init } from 'echarts'
import theme from '@/composables/useChart/theme'
import type { Direction } from '@/components/ui/dropdown.vue'
import type { ECharts } from 'echarts'

const props = defineProps<{
    instance?: ECharts
    title?: string
    direction: Direction
    bordered?: boolean
}>()

/**
 * Translations.
 */
const { t } = useI18n()

/**
 * Fake SVG instance.
 */
const svgInstance = () =>
    init(document.createElement('div'), theme, {
        renderer: 'svg',
        width,
        height
    })

/**
 * Fake Canvas instance.
 */
const canvasInstance = () =>
    init(document.createElement('div'), theme, {
        renderer: 'canvas',
        width,
        height
    })

/**
 * Export image for the corresponding format.
 */
const { menuItems } = useImageExport({
    title: () => props.title ?? 'Image',
    async getInstance(type) {
        const instance = type === 'svg' ? svgInstance() : canvasInstance()

        const option = props.instance?.getOption() || {}

        instance.setOption({ ...option, animation: false }, true)

        const exportOption = (option.export || {}) as any
        exportOption.title ??= {}
        exportOption.title.textStyle ??= {}
        exportOption.title.textStyle.width = width * 0.95
        exportOption.title.textStyle.overflow = 'break'
        exportOption.title.textStyle.color ??= '#000'
        exportOption.title.textStyle.fontSize ??= 14
        exportOption.title.textStyle.lineHeight ??= 16
        exportOption.title.text ??= props.title

        exportOption.graphic ??= []
        exportOption.graphic.push(
            getSource(
                t('source', {
                    url: location.href
                }),
                width * 0.95
            )
        )

        exportOption.graphic.push(await getWatermark())

        instance.setOption(exportOption)
        instance.resize()

        return instance
    }
})
</script>

<template>
    <UiDropdown
        :title="t('export-image')"
        :items="menuItems"
        :direction="direction"
    >
        <template #trigger-content>
            <PhotoIcon
                class="ease h-6 transition-colors duration-500 hover:text-deep-blue"
                :class="{
                    'h-8 rounded border border-deep-blue p-1': bordered
                }"
            />
        </template>
    </UiDropdown>
</template>